import { detectRuntime } from './runtime';
// Static import — the previous dynamic `await import('@capacitor/geolocation')`
// was hanging in the Capacitor WebView on first launch (see Forgejo
// #13). Vite's chunked output for that dynamic import sometimes never
// resolved when loaded under the WebView's https://localhost scheme
// handler, leaving the whole permission-check pipeline pending with no
// thrown error to catch. Static import bakes the plugin's JS into the
// main bundle — costs the web PWA ~3 KB (it's behind a runtime check
// and never executes there) in exchange for guaranteed availability
// in the APK.
import { Geolocation } from '@capacitor/geolocation';

/**
 * Permission state for the two sensors we need to record a trip.
 *
 *   granted     — sensor is usable now
 *   prompt      — we can ask; user has not been asked yet OR can still be re-asked
 *   denied      — user said no; OS will not re-prompt; user must change a setting
 *   unsupported — this device/browser doesn't expose the API at all (rare for both)
 */
export type PermissionState = 'granted' | 'prompt' | 'denied' | 'unsupported';

/**
 * iOS Safari quirks:
 *   - navigator.permissions.query for geolocation OFTEN returns 'prompt'
 *     even after the user has just granted — so we can't trust that alone.
 *   - DeviceMotionEvent.requestPermission() has no symmetric checker; the
 *     only way to ask "do I already have it" is to call requestPermission
 *     again (which would re-prompt).
 *
 * Workaround: when the user grants, persist a timestamp in localStorage.
 * On subsequent loads we surface 'granted' from the cache if it's recent
 * — paired with monotonic merging (never downgrade 'granted' to 'prompt')
 * this prevents the prompt-after-grant flicker.
 *
 * The cache is per-permission and capped at 6h since iOS may quietly
 * revoke; the user's first denial inside that window will replace the
 * cached entry.
 */
const CACHE_KEY = 'joltstream:perm:v1';
const CACHE_TTL_MS = 6 * 60 * 60 * 1000;

type CacheEntry = { state: PermissionState; ts: number };
type Cache = { location?: CacheEntry; motion?: CacheEntry };

function readCache(): Cache {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as Cache) : {};
  } catch {
    return {};
  }
}
function writeCache(c: Cache): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(c));
  } catch {
    // Private browsing / storage quota — silent fallback to no caching.
  }
}
function cachedGranted(which: keyof Cache): boolean {
  const e = readCache()[which];
  if (!e) return false;
  if (e.state !== 'granted') return false;
  return Date.now() - e.ts < CACHE_TTL_MS;
}
function rememberGrant(which: keyof Cache, state: PermissionState): void {
  const c = readCache();
  c[which] = { state, ts: Date.now() };
  writeCache(c);
}

export interface PermissionStatus {
  location: PermissionState;
  motion: PermissionState;
}

/** Probe both permissions without prompting the user. */
export async function checkPermissions(): Promise<PermissionStatus> {
  // Race the actual checks against a 5 s timeout. If anything in the
  // chain hangs (native bridge never returns, etc.) we surface a real
  // error instead of leaving the UI stuck at "checking" forever.
  return Promise.race<PermissionStatus>([
    Promise.all([checkLocation(), checkMotion()]).then(([l, m]) => ({
      location: l,
      motion: m,
    })),
    new Promise((resolve) =>
      setTimeout(() => {
        note('checkPermissions timed out after 5s');
        resolve({ location: 'prompt', motion: 'prompt' });
      }, 5000),
    ),
  ]);
}

/**
 * Module-level capture of the most recent permission-flow error.
 * Surfaced in RecordPage's diagnostics panel — much faster to spot
 * a 'plugin not implemented' than reading Logcat.
 */
let _lastPermError: string | null = null;
export function getLastPermError(): string | null {
  return _lastPermError;
}
function note(msg: string): void {
  _lastPermError = msg;
  // Also log to console so Logcat / browser devtools captures it.
  // eslint-disable-next-line no-console
  console.warn('[joltstream/perm]', msg);
}

/**
 * Capacitor permission states use four values where 'prompt-with-rationale'
 * is Android's "user denied once but we may re-ask" state. Treat it as
 * 'prompt' for the UI — Allow can still trigger the native dialog.
 */
function mapCapacitorState(s: string): PermissionState {
  if (s === 'granted') return 'granted';
  if (s === 'denied') return 'denied';
  return 'prompt';
}

async function checkLocation(): Promise<PermissionState> {
  if (detectRuntime() === 'capacitor') {
    try {
      const r = await Geolocation.checkPermissions();
      return mapCapacitorState(r.location);
    } catch (e) {
      note(`checkLocation (capacitor) failed: ${e instanceof Error ? e.message : String(e)}`);
      return 'prompt';
    }
  }
  if (!('geolocation' in navigator)) return 'unsupported';
  // localStorage cache is authoritative if recent (covers the iOS Safari
  // navigator.permissions returning 'prompt' after a fresh grant).
  if (cachedGranted('location')) return 'granted';
  if (!('permissions' in navigator)) return 'prompt';
  try {
    const r = await navigator.permissions.query({ name: 'geolocation' });
    return r.state as PermissionState;
  } catch (e) {
    note(`checkLocation (browser) query failed: ${e instanceof Error ? e.message : String(e)}`);
    return 'prompt';
  }
}

async function checkMotion(): Promise<PermissionState> {
  // Android: motion sensors don't require a runtime permission, so the
  // Capacitor path lands here too and reports granted.
  if (typeof DeviceMotionEvent === 'undefined') return 'unsupported';
  const dme = DeviceMotionEvent as unknown as {
    requestPermission?: () => Promise<'granted' | 'denied'>;
  };
  if (typeof dme.requestPermission !== 'function') return 'granted';
  // iOS: there's no read-only API for DeviceMotion state. Use the cache.
  if (cachedGranted('motion')) return 'granted';
  return 'prompt';
}

/**
 * Trigger the OS permission UI for location.
 *
 *  - Capacitor: calls Geolocation.requestPermissions, which fires the
 *    Android runtime dialog (or returns the existing state if the user
 *    has tapped 'Don't ask again').
 *  - Browser: triggers the browser's prompt by attempting a one-shot
 *    getCurrentPosition.
 *
 * Must be called from a user gesture (button click).
 */
export async function requestLocation(): Promise<PermissionState> {
  if (detectRuntime() === 'capacitor') {
    try {
      const r = await Geolocation.requestPermissions({ permissions: ['location'] });
      const next = mapCapacitorState(r.location);
      rememberGrant('location', next);
      // Clear last error on success
      if (next === 'granted') _lastPermError = null;
      return next;
    } catch (e) {
      note(`requestLocation (capacitor) failed: ${e instanceof Error ? e.message : String(e)}`);
      return 'denied';
    }
  }
  if (!('geolocation' in navigator)) return 'unsupported';
  return new Promise<PermissionState>((resolve) => {
    navigator.geolocation.getCurrentPosition(
      () => {
        rememberGrant('location', 'granted');
        _lastPermError = null;
        resolve('granted');
      },
      (e) => {
        const next = e.code === e.PERMISSION_DENIED ? 'denied' : 'prompt';
        rememberGrant('location', next);
        note(`requestLocation (browser) error code=${e.code} msg=${e.message}`);
        resolve(next);
      },
      { enableHighAccuracy: false, maximumAge: 60_000, timeout: 10_000 },
    );
  });
}

/**
 * Trigger DeviceMotion permission UI. Only iOS Safari needs it; everywhere
 * else (including Android Capacitor) we return 'granted' immediately
 * because motion sensors have no runtime permission gate.
 */
export async function requestMotion(): Promise<PermissionState> {
  if (typeof DeviceMotionEvent === 'undefined') return 'unsupported';
  const dme = DeviceMotionEvent as unknown as {
    requestPermission?: () => Promise<'granted' | 'denied'>;
  };
  if (typeof dme.requestPermission !== 'function') return 'granted';
  try {
    const status = await dme.requestPermission();
    rememberGrant('motion', status);
    if (status === 'granted') _lastPermError = null;
    return status;
  } catch (e) {
    note(`requestMotion failed: ${e instanceof Error ? e.message : String(e)}`);
    return 'denied';
  }
}

/** Per-runtime guidance for the denied state. */
export function howToReEnable(what: 'location' | 'motion'): string {
  if (detectRuntime() === 'capacitor') {
    // Inside the installed APK — direct the user to the Android app
    // settings page. There is no address bar; the Chrome instructions
    // would be nonsense.
    const sensor = what === 'location' ? 'Location' : 'Sensors';
    return (
      `Open your phone's Settings app → Apps → joltstream → Permissions → ${sensor} → Allow. ` +
      'Then come back here.'
    );
  }
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/.test(ua)) {
    return 'On iOS: open Settings → Safari → Location (or Motion & Orientation), then return here and reload.';
  }
  if (/Android/.test(ua)) {
    return "On Android Chrome: tap the address bar's site-info icon (⓵ or ⓘ) → Permissions → Reset, then reload.";
  }
  return 'Open the site-info icon next to the address bar and reset permissions, then reload.';
}
